import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ChevronDown, 
  ChevronUp, 
  Mail, 
  Phone, 
  MapPin,
  MessageCircle
} from 'lucide-react';
import { useState } from 'react';

const faqs = [
  {
    question: "What is included in the trip price?",
    answer: "Our trip prices typically include accommodations, specified meals (usually breakfast), ground transportation, guided tours, and entrance fees to attractions listed in the itinerary. International airfare, travel insurance, and personal expenses are not included unless otherwise specified."
  },
  {
    question: "How do payment plans work?",
    answer: "We offer flexible payment plans allowing you to split your trip cost into 3, 6, or 12 monthly installments. Simply select your preferred plan at checkout. There are no hidden fees or interest charges - the total amount remains the same whether you pay in full or use a payment plan."
  },
  {
    question: "Can I customize a trip or book for a private group?",
    answer: "Absolutely! All of our itineraries can be customized for private groups on dates that work for you. Contact us to discuss your group size, preferred dates, and any modifications you'd like to make to the itinerary."
  },
  {
    question: "What's the difference between Group and Solo trips?",
    answer: "Group trips run on scheduled dates with other travelers (typically 8-15 people). Solo adventures are designed for independent travelers but can also be booked by groups on custom dates. Both options include professional guides and full support."
  },
  {
    question: "What if I need to cancel my booking?",
    answer: "We understand plans can change. Cancellation policies vary by trip and timing. Generally, cancellations made 60+ days before departure receive a full refund minus a processing fee. Please review the specific terms for your trip or contact us for details."
  },
  {
    question: "Do I need travel insurance?",
    answer: "While not mandatory, we strongly recommend comprehensive travel insurance that covers trip cancellation, medical emergencies, and evacuation. We can recommend trusted providers upon request."
  },
  {
    question: "What documents do I need to travel?",
    answer: "Requirements vary by destination. Generally, you'll need a valid passport (with 6+ months validity) and may need visas. Check our Travel Hub resources for destination-specific entry requirements, or contact us for guidance."
  },
  {
    question: "How fit do I need to be for these trips?",
    answer: "Activity levels vary by trip. Most of our itineraries involve moderate walking and are suitable for average fitness levels. Check the specific trip details for activity level indicators, and reach out if you have specific concerns."
  }
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-muted-foreground text-lg">
            Find answers to common questions about our trips and services.
          </p>
        </div>

        <div className="space-y-4 mb-12">
          {faqs.map((faq, idx) => (
            <Card key={idx} className="border overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                className="w-full text-left p-5 flex items-center justify-between hover:bg-muted/30 transition-colors"
                data-testid={`faq-${idx}`}
              >
                <span className="font-medium text-primary pr-4">{faq.question}</span>
                {openIndex === idx ? (
                  <ChevronUp className="h-5 w-5 text-secondary flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                )}
              </button>
              {openIndex === idx && (
                <CardContent className="pt-0 pb-5 px-5 border-t bg-muted/20">
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Contact CTA */}
        <Card className="bg-secondary/10 border-0">
          <CardContent className="p-8 text-center">
            <MessageCircle className="h-10 w-10 text-secondary mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">
              Still have questions?
            </h3>
            <p className="text-muted-foreground mb-4">
              Our team is here to help you plan your perfect adventure.
            </p>
            <Link to="/contact">
              <Button className="bg-secondary hover:bg-secondary/90 text-white rounded-full">
                Contact Us
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
